import type { PermissionAction, PermissionResource, UserRole } from "@/lib/types/auth.types"
import { mainMenu, secondaryMenu } from "@/config/menu"

export const APP_SHORTCUT_COMMANDS = {
  shortcutsOpen: "shortcuts.open",
  searchFocus: "search.focus",
  profileOpen: "profile.open",
  authLogout: "auth.logout",
} as const

export type AppShortcutCommandId =
  | (typeof APP_SHORTCUT_COMMANDS)[keyof typeof APP_SHORTCUT_COMMANDS]
  | `navigate.${string}`

export interface ShortcutCommandDefinition {
  id: AppShortcutCommandId
  label: string
  shortcut: string
  path?: string
  requiresAuth?: boolean
  permission?: {
    resource: PermissionResource
    action: PermissionAction
  }
  allowedRoles?: UserRole[]
}

const NAVIGATION_SHORTCUTS: Record<string, string> = {
  dashboard: "ctrl+shift+d",
  reports: "ctrl+shift+r",
  settings: "ctrl+shift+t",
  overview: "ctrl+shift+o",
  appointments: "ctrl+shift+a",
  services: "ctrl+shift+s",
  customers: "ctrl+shift+c",
  users: "ctrl+shift+u",
}

const menuItemsById = [...mainMenu, ...secondaryMenu].reduce<Record<string, (typeof mainMenu)[number]>>(
  (acc, item) => {
    acc[item.id] = item
    return acc
  },
  {},
)

const navigationCommands: ShortcutCommandDefinition[] = Object.entries(NAVIGATION_SHORTCUTS).flatMap(
  ([menuId, shortcut]) => {
    const item = menuItemsById[menuId]
    if (!item) {
      return []
    }

    return [
      {
        id: `navigate.${menuId}`,
        label: `Go to ${item.label}`,
        shortcut,
        path: item.path,
        requiresAuth: true,
        permission: {
          resource: item.resource,
          action: item.action,
        },
        allowedRoles: item.allowedRoles,
      },
    ]
  },
)

export const SHORTCUT_COMMAND_DEFINITIONS: ShortcutCommandDefinition[] = [
  {
    id: APP_SHORTCUT_COMMANDS.shortcutsOpen,
    label: "Keyboard shortcuts",
    shortcut: "ctrl+shift+k",
  },
  {
    id: APP_SHORTCUT_COMMANDS.searchFocus,
    label: "Focus search",
    shortcut: "ctrl+shift+f",
  },
  {
    id: APP_SHORTCUT_COMMANDS.profileOpen,
    label: "Profile",
    shortcut: "ctrl+shift+p",
    path: "/business/profile",
    requiresAuth: true,
  },
  {
    id: APP_SHORTCUT_COMMANDS.authLogout,
    label: "Log out",
    shortcut: "ctrl+shift+l",
    requiresAuth: true,
  },
  ...navigationCommands,
]

export const SHORTCUT_COMMANDS_BY_ID = SHORTCUT_COMMAND_DEFINITIONS.reduce<
  Record<string, ShortcutCommandDefinition>
>((acc, command) => {
  acc[command.id] = command
  return acc
}, {})

